import {
  Globe,
  Terminal,
  Cpu,
  Webhook,
  Server,
  Shield,
  Cloud,
  User,
  Wrench,
  Sparkles,
  HardHat,
  Calculator,
  Briefcase,
  BookOpen,
  type LucideIcon,
} from "lucide-react";

export interface Template {
  id: string;
  name: string;
  desc: string;
  icon: LucideIcon;
  role: string;
  department: string;
  adapterType: string;
  vmSize: string;
  capabilities: string;
  recommended?: boolean;
}

export const TEMPLATES: Template[] = [
  {
    id: "it-ops",
    name: "IT Operations",
    desc: "Triage helpdesk tickets, run diagnostics, schedule maintenance windows.",
    icon: HardHat,
    role: "ic",
    department: "IT",
    adapterType: "openclaw_gateway",
    vmSize: "Standard_B2s",
    recommended: true,
    capabilities: "Answer L1 tickets · Run diagnostics · Query logs · Restart services",
  },
  {
    id: "finance",
    name: "Finance Analyst",
    desc: "Monthly reconciliations, variance analysis and exec-ready summaries.",
    icon: Calculator,
    role: "ic",
    department: "Finance",
    adapterType: "anthropic_native",
    vmSize: "Standard_B2s",
    capabilities: "Reconcile ledgers · Variance analysis · Board-pack summaries",
  },
  {
    id: "hr-coach",
    name: "HR Coach",
    desc: "Employee Q&A, policy lookups, onboarding new hires.",
    icon: Briefcase,
    role: "ic",
    department: "People",
    adapterType: "openclaw_gateway",
    vmSize: "Standard_B1s",
    capabilities: "Policy Q&A · New-hire onboarding · Leave balances",
  },
  {
    id: "research",
    name: "Research Analyst",
    desc: "Long-context research, competitive scans, market briefs.",
    icon: BookOpen,
    role: "ic",
    department: "Strategy",
    adapterType: "anthropic_native",
    vmSize: "Standard_B4ms",
    capabilities: "Market scans · Competitor briefs · Literature review",
  },
  {
    id: "blank",
    name: "Start blank",
    desc: "Configure every field manually. No presets.",
    icon: Sparkles,
    role: "ic",
    department: "",
    adapterType: "openclaw_gateway",
    vmSize: "Standard_B2s",
    capabilities: "",
  },
];

export interface Adapter {
  /** Unique card id (may differ from the submitted adapterType for runtime-mode variants) */
  id: string;
  /** Actual adapterType submitted to the API; defaults to `id` */
  adapterType?: string;
  /** Stamped into adapterConfig.runtimeMode when this card is selected */
  runtimeMode?: string;
  label: string;
  icon: LucideIcon;
  desc: string;
  advanced?: boolean;
}

/** Resolve the wizard card matching an adapterType + adapterConfig.runtimeMode pair. */
export function findAdapterCard(
  adapterType: string | undefined | null,
  runtimeMode?: unknown,
): Adapter | undefined {
  return (
    ADAPTERS.find(
      (a) => (a.adapterType ?? a.id) === adapterType && a.runtimeMode === runtimeMode,
    ) ?? ADAPTERS.find((a) => (a.adapterType ?? a.id) === adapterType && !a.runtimeMode)
  );
}

export const ADAPTERS: Adapter[] = [
  {
    id: "openclaw_gateway",
    label: "OpenClaw Agent",
    icon: Globe,
    desc: "Full Claude Code agent with MCP support. Best for most cases.",
  },
  {
    id: "anthropic_native",
    label: "Anthropic Native",
    icon: Sparkles,
    desc: "Native Claude Agent SDK on an Azure VM. No OpenClaw runtime.",
  },
  {
    id: "anthropic_native_claude_code",
    adapterType: "anthropic_native",
    runtimeMode: "claude-code",
    label: "Native — Claude Code",
    icon: Terminal,
    desc: "Real Claude Code on an Azure VM using the tenant's Claude OAuth token. Each chat links to a persistent Claude Code session.",
  },
  {
    id: "proclaw_runtime",
    label: "ProClaw Runtime",
    icon: Server,
    desc: "Native ProClaw runtime on an Azure VM. Foundry-ready, broker-authed, no OpenClaw.",
  },
  {
    id: "claude_code",
    label: "Claude Code",
    icon: Terminal,
    desc: "Claude Code CLI agent on a remote host.",
  },
  {
    id: "process",
    label: "Subprocess",
    icon: Cpu,
    desc: "Any subprocess with a custom command. Advanced.",
    advanced: true,
  },
  {
    id: "http",
    label: "HTTP Webhook",
    icon: Webhook,
    desc: "External agent reachable via webhook. Advanced.",
    advanced: true,
  },
];

export interface VmSize {
  id: string;
  label: string;
  spec: string;
  cost: number;
  tier: string;
  note: string;
  recommended?: boolean;
}

export const VM_SIZES: VmSize[] = [
  { id: "Standard_B1s", label: "B1s", spec: "1 vCPU · 1 GB", cost: 7, tier: "Light", note: "Low traffic, dev agents" },
  { id: "Standard_B2s", label: "B2s", spec: "2 vCPU · 4 GB", cost: 30, tier: "Standard", note: "Most IC agents", recommended: true },
  { id: "Standard_B2ms", label: "B2ms", spec: "2 vCPU · 8 GB", cost: 60, tier: "Standard", note: "Long-running tasks" },
  { id: "Standard_B4ms", label: "B4ms", spec: "4 vCPU · 16 GB", cost: 120, tier: "Heavy", note: "Research, long context" },
  { id: "Standard_D2s_v3", label: "D2s v3", spec: "2 vCPU · 8 GB", cost: 70, tier: "Standard", note: "Quota fallback when B-series unavailable" },
  { id: "Standard_D4s_v3", label: "D4s v3", spec: "4 vCPU · 16 GB", cost: 140, tier: "Heavy", note: "Quota fallback — D*s_v5 alternative" },
  { id: "Standard_D8s_v3", label: "D8s v3", spec: "8 vCPU · 32 GB", cost: 280, tier: "Max", note: "Quota fallback — D*s_v5 alternative" },
  { id: "Standard_D4s_v5", label: "D4s v5", spec: "4 vCPU · 16 GB", cost: 140, tier: "Heavy", note: "High concurrency" },
  { id: "Standard_D8s_v5", label: "D8s v5", spec: "8 vCPU · 32 GB", cost: 280, tier: "Max", note: "Multi-tool pipelines" },
];

export interface Region {
  id: string;
  label: string;
  city: string;
}

export const REGIONS: Region[] = [
  { id: "uksouth", label: "UK South", city: "London" },
  { id: "westeurope", label: "West Europe", city: "Amsterdam" },
  { id: "northeurope", label: "North Europe", city: "Dublin" },
  { id: "eastus", label: "East US", city: "Virginia" },
  { id: "westus2", label: "West US 2", city: "Washington" },
  { id: "australiaeast", label: "Australia East", city: "Sydney" },
];

export interface Phase {
  id: string;
  title: string;
  desc: string;
  icon: LucideIcon;
  duration: number;
  steps: string[];
}

export const PHASES: Phase[] = [
  {
    id: "validate",
    title: "Validating configuration",
    desc: "Checking permissions, quotas and naming.",
    icon: Shield,
    duration: 2,
    steps: [
      "Checking Entra ID admin consent",
      "Verifying Azure subscription quotas",
      "Reserving agent name across tenant",
    ],
  },
  {
    id: "allocate",
    title: "Allocating VM",
    desc: "Requesting a $VM_SIZE instance in $REGION.",
    icon: Cloud,
    duration: 45,
    steps: [
      "Submitting ARM template to $REGION",
      "Provisioning $VM_SIZE virtual machine",
      "Attaching managed disk (30 GB Premium SSD)",
      "Creating network security group",
    ],
  },
  {
    id: "boot",
    title: "Booting runtime",
    desc: "Installing OpenClaw and dependencies.",
    icon: Server,
    duration: 50,
    steps: [
      "Booting Ubuntu 22.04 LTS",
      "Installing OpenClaw gateway runtime",
      "Installing Claude Code CLI",
      "Registering agent with ProClaw control-plane",
    ],
  },
  {
    id: "identity",
    title: "Generating identity",
    desc: "Writing workspace files and avatar.",
    icon: User,
    duration: 12,
    steps: [
      "Generating CLAUDE.md and soul.md",
      "Generating agent avatar",
      "Writing role, department and capabilities",
    ],
  },
  {
    id: "health",
    title: "Health check",
    desc: "First handshake with the agent.",
    icon: Wrench,
    duration: 6,
    steps: [
      "Pinging OpenClaw gateway",
      "Verifying heartbeat channel",
      "Running first diagnostic prompt",
    ],
  },
];

export function fillTemplate(text: string, vmLabel: string, regionLabel: string): string {
  return text.replace("$VM_SIZE", vmLabel).replace("$REGION", regionLabel);
}
